import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { ColumnType } from 'antd/lib/table';
import { BaseModal } from '@app/components/common/BaseModal/BaseModal';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { BaseTableCellTextWrapper } from '@app/components/common/BaseTable/BaseTableCells.styles';
import { BaseText } from '@app/components/common/BaseText/BaseText';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { Option } from '@app/components/common/selects/BaseSelect/BaseSelect';
import {
  createNetNames,
  deleteNetName,
  getAllNetNames,
  getNetTypeEntrypoint,
  updateNetName,
} from '@app/api/professionalFisher/netName.api';
import { IProfessionalFisherNetName, IProfessionalFisherNetTypeOption } from '@app/interfaces/professionalFisher';
import { getLocalizedValue } from '@app/utils/utils';
import { DeleteEntryModal } from './DeleteEntryModal';

import * as S from './NetListModal.styles';

interface INewNetRow {
  name: string;
  type: string | null;
  length: number | null;
  height: number | null;
  mesh: number | null;
}

const emptyNetRow: INewNetRow = { name: '', type: null, length: null, height: null, mesh: null };

interface INetListTableRow {
  key: string;
  existing?: IProfessionalFisherNetName;
  newIndex?: number;
}

interface IProps {
  isVisible: boolean;
  onClose: () => void;
}

export const NetListModal: React.FC<IProps> = ({ isVisible, onClose }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [netNames, setNetNames] = useState<IProfessionalFisherNetName[]>([]);
  const [typeOptions, setTypeOptions] = useState<IProfessionalFisherNetTypeOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [newRows, setNewRows] = useState<INewNetRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingNetName, setDeletingNetName] = useState<IProfessionalFisherNetName | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<INewNetRow>(emptyNetRow);
  const [isEditSaving, setIsEditSaving] = useState(false);

  const fetchNetNames = async () => {
    const { error, data } = await getAllNetNames();
    if (!error && data) {
      setNetNames(data.filter((netName) => !netName.softDeleted));
    }
  };

  useEffect(() => {
    if (!isVisible) {
      setNewRows([]);
      setIsSaving(false);
      setDeletingNetName(null);
      setIsDeleting(false);
      setEditingId(null);
      setEditRow(emptyNetRow);
      setIsEditSaving(false);
      return;
    }

    void (async function fetchData() {
      setIsLoading(true);
      const [, entrypointResult] = await Promise.all([fetchNetNames(), getNetTypeEntrypoint()]);
      if (!entrypointResult.error && entrypointResult.data) {
        setTypeOptions(entrypointResult.data.netTypeOptions ?? []);
      }
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const handleAddRow = () => {
    setNewRows((rows) => [...rows, { ...emptyNetRow }]);
  };

  const handleRemoveRow = (index: number) => {
    setNewRows((rows) => rows.filter((_, i) => i !== index));
  };

  const handleRowChange = (index: number, rowData: INewNetRow) => {
    setNewRows((rows) => rows.map((row, i) => (i === index ? rowData : row)));
  };

  const isRowComplete = (row: INewNetRow) =>
    row.name.trim() !== '' && Boolean(row.type) && row.length != null && row.height != null && row.mesh != null;

  const isValid = newRows.length > 0 && newRows.every(isRowComplete);

  const handleSave = async () => {
    if (!isValid) {
      return;
    }

    setIsSaving(true);
    const { error } = await createNetNames(
      newRows.map((row) => ({
        id: null,
        name: row.name.trim(),
        type: row.type as string,
        length: row.length,
        height: row.height,
        mesh: row.mesh,
      })),
    );

    if (error) {
      setIsSaving(false);
      Modal.error({ title: t('common.error') });
      return;
    }

    setNewRows([]);
    await fetchNetNames();
    setIsSaving(false);
  };

  const handleStartEdit = (netName: IProfessionalFisherNetName) => {
    setEditingId(netName.id);
    setEditRow({
      name: netName.name,
      type: netName.type,
      length: netName.length,
      height: netName.height,
      mesh: netName.mesh,
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditRow(emptyNetRow);
  };

  const handleSaveEdit = async () => {
    if (!editingId || !isRowComplete(editRow)) {
      return;
    }

    setIsEditSaving(true);
    const { error } = await updateNetName(editingId, {
      name: editRow.name.trim(),
      type: editRow.type as string,
      length: editRow.length,
      height: editRow.height,
      mesh: editRow.mesh,
    });

    if (error) {
      setIsEditSaving(false);
      Modal.error({ title: t('common.error') });
      return;
    }

    setEditingId(null);
    setEditRow(emptyNetRow);
    await fetchNetNames();
    setIsEditSaving(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingNetName) {
      return;
    }

    setIsDeleting(true);
    const { error } = await deleteNetName(deletingNetName.id);
    setIsDeleting(false);
    setDeletingNetName(null);

    if (error) {
      Modal.error({ title: t('common.error') });
      return;
    }

    await fetchNetNames();
  };

  const getOptionLabel = (option: IProfessionalFisherNetTypeOption): string =>
    getLocalizedValue(option, 'label', locale, 'capitalize') || option.labelEn || option.value;

  const getTypeLabel = (value: string): string => {
    const option = typeOptions.find((typeOption) => typeOption.value === value);
    return option ? getOptionLabel(option) : value;
  };

  const tableRows: INetListTableRow[] = [
    ...netNames.map((netName) => ({ key: netName.id, existing: netName })),
    ...newRows.map((_, index) => ({ key: `new-${index}`, newIndex: index })),
  ];

  const renderNumberCell =
    (field: 'length' | 'height' | 'mesh') =>
    (_: unknown, record: INetListTableRow): React.ReactNode => {
      if (record.existing) {
        if (record.existing.id === editingId) {
          return (
            <S.BaseInput
              size="small"
              type="number"
              min={0}
              value={editRow[field] ?? ''}
              disabled={isEditSaving}
              onChange={(e) => setEditRow({ ...editRow, [field]: e.target.value === '' ? null : +e.target.value })}
            />
          );
        }

        return <BaseTableCellTextWrapper>{record.existing[field] ?? '-'}</BaseTableCellTextWrapper>;
      }

      const row = newRows[record.newIndex as number];
      return (
        <S.BaseInput
          size="small"
          type="number"
          min={0}
          value={row[field] ?? ''}
          disabled={isSaving}
          onChange={(e) =>
            handleRowChange(record.newIndex as number, {
              ...row,
              [field]: e.target.value === '' ? null : +e.target.value,
            })
          }
        />
      );
    };

  const columns: ColumnType<INetListTableRow>[] = [
    {
      title: t('common.name'),
      key: 'name',
      render: (_, record) => {
        if (record.existing) {
          if (record.existing.id === editingId) {
            return (
              <S.BaseInput
                size="small"
                value={editRow.name}
                disabled={isEditSaving}
                onChange={(e) => setEditRow({ ...editRow, name: e.target.value })}
              />
            );
          }

          return <BaseTableCellTextWrapper>{record.existing.name}</BaseTableCellTextWrapper>;
        }

        const row = newRows[record.newIndex as number];
        return (
          <S.BaseInput
            size="small"
            value={row.name}
            disabled={isSaving}
            onChange={(e) => handleRowChange(record.newIndex as number, { ...row, name: e.target.value })}
          />
        );
      },
    },
    {
      title: t('common.type'),
      key: 'type',
      width: 150,
      render: (_, record) => {
        if (record.existing) {
          if (record.existing.id === editingId) {
            return (
              <S.TypeSelect
                className="ant-light-dropdown"
                popupClassName="ant-light-dropdown"
                size="small"
                disabled={isEditSaving}
                value={editRow.type ?? undefined}
                onChange={(value) => setEditRow({ ...editRow, type: value as string })}
              >
                {typeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {getOptionLabel(option)}
                  </Option>
                ))}
              </S.TypeSelect>
            );
          }

          return (
            <BaseTableCellTextWrapper>
              {record.existing.type ? getTypeLabel(record.existing.type) : '-'}
            </BaseTableCellTextWrapper>
          );
        }

        const row = newRows[record.newIndex as number];
        return (
          <S.TypeSelect
            className="ant-light-dropdown"
            popupClassName="ant-light-dropdown"
            size="small"
            disabled={isSaving}
            value={row.type ?? undefined}
            onChange={(value) => handleRowChange(record.newIndex as number, { ...row, type: value as string })}
          >
            {typeOptions.map((option) => (
              <Option key={option.value} value={option.value}>
                {getOptionLabel(option)}
              </Option>
            ))}
          </S.TypeSelect>
        );
      },
    },
    {
      title: t('professionalFishers.lengthCm'),
      key: 'length',
      width: 110,
      render: renderNumberCell('length'),
    },
    {
      title: t('professionalFishers.heightCm'),
      key: 'height',
      width: 110,
      render: renderNumberCell('height'),
    },
    {
      title: t('professionalFishers.meshSizeCm'),
      key: 'mesh',
      width: 120,
      render: renderNumberCell('mesh'),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => {
        if (record.existing && record.existing.id === editingId) {
          return (
            <S.ActionsWrapper>
              <S.IconButton
                type="button"
                disabled={isEditSaving || !isRowComplete(editRow)}
                onClick={handleSaveEdit}
                aria-label={t('common.save')}
              >
                <CheckOutlined />
              </S.IconButton>
              <S.IconButton
                type="button"
                disabled={isEditSaving}
                onClick={handleCancelEdit}
                aria-label={t('common.cancel')}
              >
                <CloseOutlined />
              </S.IconButton>
            </S.ActionsWrapper>
          );
        }

        return (
          <S.ActionsWrapper>
            {record.existing && (
              <S.IconButton
                type="button"
                disabled={isSaving || editingId != null}
                onClick={() => handleStartEdit(record.existing as IProfessionalFisherNetName)}
                aria-label={t('common.edit')}
              >
                <EditOutlined />
              </S.IconButton>
            )}
            <S.DeleteIconButton
              type="button"
              disabled={isSaving || editingId != null}
              onClick={() =>
                record.existing ? setDeletingNetName(record.existing) : handleRemoveRow(record.newIndex as number)
              }
              aria-label={t('common.delete')}
            >
              <DeleteOutlined />
            </S.DeleteIconButton>
          </S.ActionsWrapper>
        );
      },
    },
  ];

  return (
    <BaseModal
      open={isVisible}
      footer={null}
      onCancel={onClose}
      centered
      width={1000}
      closable={!isSaving && !isEditSaving}
      maskClosable={!isSaving && !isEditSaving}
    >
      <S.BodyWrapper>
        <S.TitleWrapper>
          <BaseText color="dark" size="xxxl" weight="bold">
            {t('professionalFishers.netList')}
          </BaseText>
        </S.TitleWrapper>

        <S.InnerWrapper>
          <BaseTable
            light
            bordered
            columns={columns}
            dataSource={tableRows}
            pagination={false}
            loading={isLoading}
            rowKey="key"
            scroll={{ x: 'max-content' }}
          />

          <S.AddingButtonWrapper type="button" disabled={isSaving || editingId != null} onClick={handleAddRow}>
            <PlusCircleOutlined />
            <S.AddingButtonText>{t('professionalFishers.addNewNet')}</S.AddingButtonText>
          </S.AddingButtonWrapper>
        </S.InnerWrapper>
      </S.BodyWrapper>

      {newRows.length > 0 && (
        <S.FooterWrapper>
          <BaseRow justify="center">
            <S.FooterButton
              type="primary"
              shape="round"
              htmlType="button"
              loading={isSaving}
              disabled={!isValid}
              onClick={handleSave}
            >
              {t('common.save')}
            </S.FooterButton>
          </BaseRow>
        </S.FooterWrapper>
      )}

      <DeleteEntryModal
        isVisible={deletingNetName != null}
        isProcessing={isDeleting}
        onClose={() => setDeletingNetName(null)}
        onDelete={handleDeleteConfirm}
      />
    </BaseModal>
  );
};
